import React from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Chip,
  Button,
  Collapse,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  LocalOffer,
  Info,
  Security,
  Image as ImageIcon,
} from '@mui/icons-material';

// Simple Image Placeholder Component
const ImagePlaceholder = () => (
  <Box
    sx={{
      width: 50,
      height: 50,
      backgroundColor: '#f5f5f5',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 1,
    }}
  >
    <ImageIcon sx={{ fontSize: 20, color: '#ccc', mb: 0.25 }} />
    <Typography sx={{ fontSize: '0.6rem', color: '#999' }}>
      No Image
    </Typography>
  </Box>
);

const OrderSummary = ({
  items,
  subtotal,
  shipping,
  tax,
  discount = 0,
  packagingFee = 0,  // Now comes from actual packaging products
  total,
  isCheckout = false,
  onApplyCoupon,
  showDetails = true,
  showPolicies = true,
  isUpdatingPackage = false,
  hideShipping = false,
  hideTax = false,
}) => {
  const [itemsExpanded, setItemsExpanded] = React.useState(!isCheckout);
  const [couponCode, setCouponCode] = React.useState('');
  const [failedImages, setFailedImages] = React.useState(new Set());

  const handleApplyCoupon = () => {
    if (onApplyCoupon && couponCode) {
      onApplyCoupon(couponCode);
    }
  };

  // Remove savings calculation since we don't have discounts in cart anymore
  const savings = 0;

  return (
    <Paper
      elevation={isCheckout ? 3 : 1}
      sx={{
        p: { xs: 2, sm: 3 },
        position: isCheckout ? 'sticky' : 'relative',
        top: isCheckout ? 20 : 0,
      }}
    >
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Order Summary
      </Typography>

      {/* Items List */}
      {showDetails && (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary">
              {items.length} item{items.length > 1 ? 's' : ''}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setItemsExpanded(!itemsExpanded)}
            >
              {itemsExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>

          <Collapse in={itemsExpanded}>
            <List dense sx={{ mb: 2 }}>
              {items.map((item) => (
                <ListItem
                  key={item.id}
                  sx={{
                    px: 0,
                    py: 1,
                    borderBottom: '1px solid #f0f0f0',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        {/* Product Image or Placeholder */}
                        {item.image && !failedImages.has(item.id) ? (
                          <Box
                            component="img"
                            src={item.image}
                            alt={item.name}
                            onError={() => {
                              setFailedImages(prev => new Set([...prev, item.id]));
                            }}
                            sx={{
                              width: 50,
                              height: 50,
                              objectFit: 'cover',
                              borderRadius: 1,
                            }}
                          />
                        ) : (
                          <ImagePlaceholder />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {item.name}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            {item.size && (
                              <Chip
                                label={`Size: ${item.size}`}
                                size="small"
                                sx={{ height: 18, fontSize: '0.7rem' }}
                              />
                            )}
                            <Typography variant="caption" color="text.secondary">
                              Qty: {item.quantity}
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          KSH {((item.price + (item.additionalServicesAmount || 0)) * item.quantity).toLocaleString()}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Price Breakdown */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Subtotal
          </Typography>
          <Typography variant="body2">
            KSH {subtotal.toLocaleString()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Packaging Fee
          </Typography>
          {isUpdatingPackage ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} sx={{ color: 'var(--primary)' }} />
              <Typography variant="caption" color="text.secondary">
                Updating...
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2">
              {packagingFee > 0 ? `KSH ${packagingFee.toLocaleString()}` : 'Included'}
            </Typography>
          )}
        </Box>

        {!hideShipping && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Shipping
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: shipping === 0 && !isCheckout ? '#666' : shipping === 0 ? '#4caf50' : 'inherit' }}
            >
              {!isCheckout && shipping === 0 ? 'Calculated at checkout' : shipping === 0 ? 'FREE' : `KSH ${shipping.toLocaleString()}`}
            </Typography>
          </Box>
        )}

        {!hideTax && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Tax (16% VAT)
            </Typography>
            <Typography variant="body2">
              KSH {tax.toLocaleString()}
            </Typography>
          </Box>
        )}

        {discount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#4caf50' }}>
              Discount
            </Typography>
            <Typography variant="body2" sx={{ color: '#4caf50' }}>
              -KSH {discount.toLocaleString()}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Coupon Code */}
      {onApplyCoupon && !isCheckout && (
        <>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <input
                type="text"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '14px',
                }}
              />
              <Button
                variant="outlined"
                onClick={handleApplyCoupon}
                startIcon={<LocalOffer />}
                sx={{
                  borderColor: 'var(--primary)',
                  color: 'var(--primary)',
                  '&:hover': {
                    borderColor: 'var(--button-hover)',
                    backgroundColor: 'color-mix(in srgb, var(--primary) 4%, transparent)',
                  },
                }}
              >
                Apply
              </Button>
            </Box>
          </Box>
        </>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Total */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {(hideShipping || hideTax) ? 'Subtotal' : 'Total'}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          KSH {total.toLocaleString()}
        </Typography>
      </Box>

      {/* Notice about tax and shipping */}
      {(hideShipping || hideTax) && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            py: 0.5,
            '& .MuiAlert-icon': {
              fontSize: '1.2rem'
            }
          }}
        >
          <Typography variant="caption">
            Tax and shipping will be calculated at checkout
          </Typography>
        </Alert>
      )}

      {/* Savings Alert */}
      {savings > 0 && (
        <Alert
          severity="success"
          icon={<LocalOffer />}
          sx={{ mb: 2, py: 0.5 }}
        >
          You saved KSH {savings.toLocaleString()} on this order!
        </Alert>
      )}

      {/* Policies */}
      {showPolicies && (
        <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Security sx={{ fontSize: 18, color: 'var(--primary)' }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>
              Secure Checkout
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            • 30-day return policy
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            • Secure payment processing
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            • Authentic products guaranteed
          </Typography>
        </Box>
      )}

      {/* VAT Notice - Only show if tax is visible */}
      {!hideTax && (
        <Box sx={{ mt: 2, p: 1, backgroundColor: '#fff8e1', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Info sx={{ fontSize: 16, color: '#f57c00' }} />
            <Typography variant="caption" color="text.secondary">
              Prices include 16% VAT. Business customers can claim VAT refunds with proper documentation.
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default OrderSummary;